import {
  ABSOLUTE_NEGATIVE_X_AXIS,
  ABSOLUTE_NEGATIVE_Z_AXIS,
  ABSOLUTE_X_AXIS,
  ABSOLUTE_Z_AXIS,
  COLLISION_WALL_RANGE_THICKNESS,
  SNAP_RANGE_DIRECTIONAL_KEY,
} from '@constants'
import { Getter, Item, Setter, Store, Walls } from '@types'
import { toVector3 } from '@utils/conversion/toVector3'
import { getAngle2D, getWidth } from '@utils/generalMaths/geometry'
import { getBaseArea } from '@utils/sceneObjects/areas/getBaseArea'
import {
  closeFloorAndRoof,
  hydrateClosedRoomAreas,
} from '@utils/sceneObjects/areas/hydrateClosedRoomAreas'
import isHole from '@utils/sceneObjects/assertions/isHole'
import isWall from '@utils/sceneObjects/assertions/isWall'
import { dragFromLeftVertex } from '@utils/sceneObjects/vertices/dragFromLeftVertex'
import { dragFromRightVertex } from '@utils/sceneObjects/vertices/dragFromRightVertex'

import { setResource } from '@store'
import isArea from '@utils/sceneObjects/assertions/isArea'
import isLight from '@utils/sceneObjects/assertions/isLight'
import getId from '@utils/sceneObjects/getId'
import calculateWallPosition from '@utils/sceneObjects/walls/calculateWallPosition'
import { getBaseWall } from '@utils/sceneObjects/walls/getBaseWall'
import { getInnerAngle } from '@utils/sceneObjects/walls/getInnerAngle'
import getRelevantPoints from '@utils/sceneObjects/walls/getRelevantPoints'
import { getWallCOBB } from '@utils/sceneObjects/walls/getWallCOBB'
import { Matrix4 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import {
  max0,
  max180,
  max270,
  max90,
  min0,
  min180,
  min270,
  min90,
  ninetyDeg,
  oneEightyDeg,
  twoSeventyDeg,
} from './baseValues'

export type WallActions = ReturnType<typeof createWallsActions>

export const createWallsActions = (set: Setter, get: Getter) => ({
  drawWall: (x: number, z: number, id: string, oldId = '') => {
    const height = get().prefs?.ALTOPARED
    const thickness =
      get().mode === 'MODE_DRAWING_WALL' && get().lastThickness
        ? get().lastThickness
        : get().prefs?.FONDOPARED || 150

    const boundingBox = new OBB(
      toVector3([x, height / 2, z]),
      toVector3([0, height, (thickness + COLLISION_WALL_RANGE_THICKNESS) / 2]),
    )
    set((store) => {
      const increment = store.increment
      const collisionOBB = new OBB(
        toVector3([x, height / 2, z]),
        toVector3([0, height, thickness / 2]),
      )
      const oldItem = store.items.get(oldId) as Walls
      const wall = getBaseWall(
        {
          id,
          height,
          end: oldItem ? oldItem.end : [x, z],
          start: (oldItem ? oldItem.end : [x, z]) satisfies [number, number],
          thickness,
          areas:
            store.mode === 'MODE_DRAWING_WALL'
              ? new Set()
              : store.currentArea
                ? new Set([store.currentArea])
                : new Set(),
          prevWall: oldId,
        },
        store,
      ) satisfies Walls
      store.items.set(id, wall satisfies Walls)
      store.layers[store.currentLayer].selected.add(id)
      store.layers[store.currentLayer].items.add(id)
    })
  },
  editWall: (xE: number, zE: number, id: string) => {
    const updateWallHoles = () => get().updateWallHoles(id)
    set((store) => {
      if (!store.canDraw) return
      const item = store.items.get(id)! as Walls
      if (!item) return /* console.warn(`No hay item editando walla. (id: ${id})`) */

      const newWidth = toVector3(item.start).distanceTo(toVector3([xE, 0, zE]))
      const start = item.start
      const thickness = item.thickness!
      const { angle } = getAngle2D(start, [xE, zE])
      const [x, z] = calculateWallPosition(start, [xE, zE])
      const prevWall = store.items.get(item.prevWall) as Walls

      let didSnap
      switch (true) {
        //todo: CALCULAR EL END BIEN
        case angle <= max90 && angle > min90: {
          const currDirection = ABSOLUTE_Z_AXIS.clone().setLength(newWidth)
          const proyectedPosition = toVector3([x, 0, z]).projectOnVector(currDirection)
          const newNewWidth = getWidth(start, [xE, zE])
          const proyectedEndPosition = toVector3([xE, zE]).projectOnVector(proyectedPosition)
          item.rotation = ninetyDeg
          if (prevWall) {
            item.innerAngle = ninetyDeg
          }
          item.z = start[1] + newWidth / 2
          item.x = start[0] + thickness / 2
          item.width = newNewWidth
          item.end = [start[0], proyectedEndPosition.z]
          if (prevWall) {
            item.innerAngle = getInnerAngle(prevWall.start, prevWall.end, item.start, [
              start[0],
              proyectedEndPosition.z,
            ]).innerAngle
          }
          didSnap = true
          break
        }
        //This case is fucking annoying: The angle goes from 3.14 to -3.14....
        //Which means it must be bigger than 3.14-variation (min180) or bigger than -3.14 + variation (max180)
        //You also need to know that: If the angle is smaller than 0: the angle must be bigger than -3.14+variation
        //You also need to know that: If the angle is smaller than 0: the angle must be bigger than -3.14+variation
        //If the angle is bigger than 0: If the angle is bigger than 3.14-variation
        case (angle < max180 && angle < 0) || (angle > min180 && angle > 0): {
          const currDirection = ABSOLUTE_NEGATIVE_X_AXIS.clone().setLength(newWidth)
          const proyectedPosition = toVector3([x, 0, z]).projectOnVector(currDirection)
          const proyectedEnd = toVector3([xE, zE]).projectOnVector(currDirection)

          item.rotation = oneEightyDeg
          if (prevWall) {
            item.innerAngle = oneEightyDeg
          }

          item.x = proyectedPosition.x - (thickness / 2) * Math.sin(oneEightyDeg)
          item.z = start[1] - (thickness / 2) * Math.cos(oneEightyDeg)
          item.end = [proyectedEnd.x, start[1]]
          if (prevWall && prevWall.rotation === item.rotation) {
            item.z = prevWall.z
          }
          if (prevWall) {
            item.innerAngle = getInnerAngle(prevWall.start, prevWall.end, item.start, [
              proyectedEnd.x,
              start[1],
            ]).innerAngle
          }
          didSnap = true

          break
        }
        case angle > max270 && angle < min270: {
          const currDirection = ABSOLUTE_NEGATIVE_Z_AXIS.clone().setLength(newWidth)
          const proyectedPosition = toVector3([x, 0, z]).projectOnVector(currDirection)
          const proyectedEnd = toVector3([xE, zE]).projectOnVector(currDirection)

          item.rotation = twoSeventyDeg
          item.x = start[0] + (thickness / 2) * Math.sin(twoSeventyDeg)
          item.z = proyectedPosition.z
          item.end = [start[0], proyectedEnd.z]
          if (prevWall && prevWall.rotation === item.rotation) {
            item.x = prevWall.x
          }
          if (prevWall) {
            item.innerAngle = getInnerAngle(prevWall.start, prevWall.end, item.start, [
              start[0],
              proyectedEnd.z,
            ]).innerAngle
          }
          didSnap = true

          break
        }
        //In this case too:Jumps from 1 to -1 so we need to check further
        case (angle < max0 && angle > 0) || (angle > min0 && angle < 0): {
          const currDirection = ABSOLUTE_X_AXIS.clone().setLength(newWidth)
          const proyectedPosition = toVector3([x, 0, z]).projectOnVector(currDirection)
          const proyectedEnd = toVector3([xE, zE]).projectOnVector(currDirection)

          item.rotation = 0
          item.x = proyectedPosition.x - (thickness / 2) * Math.sin(0)
          item.z = start[1] - thickness / 2
          item.end = [proyectedEnd.x, start[1]]
          if (prevWall && prevWall.rotation === item.rotation) {
            item.z = prevWall.z
          }
          if (prevWall) {
            item.innerAngle = getInnerAngle(
              [...prevWall.start],
              [...prevWall.end],
              [...item.start],
              [proyectedEnd.x, start[1]],
            ).innerAngle
          }
          didSnap = true

          break
        }
        default:
          item.rotation = angle
          item.x = x + (thickness / 2) * Math.sin(angle)
          item.z = z - (thickness / 2) * Math.cos(angle)
          item.end = [xE, zE]
          if (prevWall) {
            item.start = prevWall.end
          }
          break
      }
      item.width = newWidth

      updateWallHoles()
      if (!item?.prevWall || !prevWall)
        return // console.log(
        //   `prevWall not found on wall: ${id}, and it ${didSnap ? 'snapped' : "didn't snap"}`,
        // )
      const { innerAngle } = getInnerAngle(prevWall.start, prevWall.end, start, [xE, zE])

      if (!didSnap) {
        item.innerAngle = innerAngle
      }
    })
  },
  updateWallWidthAndNext: (id: string, width: number) => {
    const updateWallHoles = get().updateWallHoles
    set((store) => {
      const wall = store.items.get(id) as Walls
      if (!wall) return

      const thickness = wall.thickness
      const rotation = wall.rotation

      const [startX, startZ] = wall.start

      const newEndX = startX + Math.cos(rotation) * width
      const newEndZ = startZ + Math.sin(rotation) * width

      const [centerX, centerZ] = calculateWallPosition([startX, startZ], [newEndX, newEndZ])

      wall.width = width
      wall.end = [newEndX, newEndZ]
      wall.x = centerX + (thickness / 2) * Math.sin(rotation)
      wall.z = centerZ - (thickness / 2) * Math.cos(rotation)

      if (wall.nextWall) {
        const nextWall = store.items.get(wall.nextWall)
        if (!isWall(nextWall)) return
        const [centerX, centerZ] = calculateWallPosition([newEndX, newEndZ], nextWall.end)
        const { angle } = getAngle2D([newEndX, newEndZ], nextWall.end)
        const innerAngle = getInnerAngle(
          [startX, startZ],
          [newEndX, newEndZ],
          [newEndX, newEndZ],
          nextWall.end,
        )
        nextWall.innerAngle = innerAngle.innerAngle
        const thicknessnextWall = nextWall.thickness / 2
        nextWall.x = centerX + thicknessnextWall * Math.sin(angle)
        nextWall.z = centerZ - thicknessnextWall * Math.cos(angle)
        nextWall.start = [newEndX, newEndZ]
        nextWall.rotation = angle
        const {
          beta,
          dotAB,
          cross,
          absoluteCross,
          absoluteDot,
          symmetricalPerpendicularPoint,
          perpendicularPoint,
        } = getRelevantPoints({
          AAngle: rotation,
          AEnd: [newEndX, newEndZ],
          AStart: [startX, startZ],
          BEnd: nextWall.end,
          AThickness: thickness,
          BAngle: angle,
          BStart: [newEndX, newEndZ],
          BThickness: nextWall.thickness,
        })
        wall.beta = beta
        wall.dot = dotAB
        wall.cross = cross
        wall.absoluteCross = absoluteCross
        wall.absoluteDot = absoluteDot
        wall.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
        wall.perpendicularPoint = perpendicularPoint
        nextWall.prevDot = dotAB
        nextWall.prevCross = cross

        const widthNext = toVector3([newEndX, newEndZ]).distanceTo(toVector3(nextWall.end))
        nextWall.width = widthNext

        updateWallHoles(id)
        if (nextWall.nextWall) {
          const nextnextWall = store.items.get(nextWall.nextWall)
          if (!isWall(nextnextWall)) return

          const innerAngleNextnextWall = getInnerAngle(
            nextWall.start,
            nextWall.end,
            nextnextWall.start,
            nextnextWall.end,
          )
          nextnextWall.innerAngle = innerAngleNextnextWall.innerAngle

          const {
            beta,
            dotAB,
            cross,
            absoluteCross,
            absoluteDot,
            symmetricalPerpendicularPoint,
            perpendicularPoint,
          } = getRelevantPoints({
            AAngle: angle,
            AEnd: nextWall.end,
            AStart: [newEndX, newEndZ],
            BEnd: nextnextWall.end,
            AThickness: nextWall.thickness,
            BAngle: nextnextWall.rotation,
            BStart: nextnextWall.start,
            BThickness: nextnextWall.thickness,
          })
          nextWall.beta = beta
          nextWall.dot = dotAB
          nextWall.cross = cross
          nextWall.absoluteCross = absoluteCross
          nextWall.absoluteDot = absoluteDot
          nextWall.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
          nextWall.perpendicularPoint = perpendicularPoint
          nextnextWall.prevDot = dotAB
          nextnextWall.prevCross = cross
          updateWallHoles(wall.nextWall)
          updateWallHoles(wall.prevWall)
          updateWallHoles(nextWall.nextWall)
        }
      }
      const firstwallId = store.currentFirstWall
      const firstwall = store.items.get(firstwallId) as Walls
      updateWallHoles(firstwallId)

      // halfway through implementation they will take me away for something else
      // trying to avoid the extra enter when closing the area
      // Assuming newEndX and newEndZ are the new 'end' coordinates for 'wall'
      if (firstwall && wall.end[0] === firstwall.start[0] && wall.end[1] === firstwall.start[1]) {
        closeFloorAndRoof(store)
        store.mode = 'MODE_IDLE'
      }
    })
  },

  editWallWidth: (id: string, width: number) => {
    const updateWallHoles = (holeID: string) => get().updateWallHoles(holeID)
    set((store) => {
      const wall = store.items.get(id) as Walls
      if (!wall) return
      const thickness = wall.thickness
      const rotation = wall.rotation

      const [startX, startZ] = wall.start

      const direction = toVector3([Math.cos(rotation), 0, Math.sin(rotation)]).setLength(width)

      const newEnd = toVector3([startX, 0, startZ]).add(direction)

      const [centerX, centerZ] = calculateWallPosition([startX, startZ], [newEnd.x, newEnd.z])

      wall.end = [newEnd.x, newEnd.z]
      wall.x = centerX + (thickness / 2) * Math.sin(rotation)
      wall.z = centerZ - (thickness / 2) * Math.cos(rotation)
      wall.width = width

      updateWallHoles(wall.id)
      const currentFirstWall = store.currentFirstWall
      const currentFirstWallValue = store.items.get(currentFirstWall) as Walls

      if (!currentFirstWallValue) {
        store.currentFirstWall = id
        wall.isFirst = true
        const areaID = getId('areas')
        store.areas.set(areaID, getBaseArea({ id: areaID, sides: [id] }))
        store.currentArea = areaID
      }

      const maxX = newEnd.x + SNAP_RANGE_DIRECTIONAL_KEY
      const minX = newEnd.x - SNAP_RANGE_DIRECTIONAL_KEY
      const maxZ = newEnd.z + SNAP_RANGE_DIRECTIONAL_KEY
      const minZ = newEnd.z - SNAP_RANGE_DIRECTIONAL_KEY
      const x = currentFirstWallValue?.start[0]
      const z = currentFirstWallValue?.start[1]

      if (x <= maxX && x >= minX && z <= maxZ && z >= minZ) {
        // @Vikms95 It seems like using actions inside actions is not viable
        // as the values do not get updated inmediatly, reusing logic for now
        const area = store.areas.get(store.currentArea)
        const roof = store.areas.get(store.currentRoof)
        if (!area) return
        area.isClosed = true

        // Unselect all items
        const currLayer = store.currentLayer
        if (!currLayer && currLayer !== 0) return
        const layers = store.layers
        if (!layers) return
        const selected = layers[currLayer].selected
        if (!selected) return
        layers[currLayer].selected.clear()

        // Add inner angle to new and first walls
        const oldWall = store.items.get(id) as Walls
        const newWall = store.items.get(store.currentFirstWall) as Walls
        const [oldStart, oldEnd] = [oldWall.start, oldWall.end]
        const [newStart, newEnd] = [newWall.start, newWall.end]
        newWall.innerAngle = getInnerAngle(oldStart, oldEnd, newStart, newEnd).innerAngle
        const {
          beta,
          dotAB,
          cross,
          absoluteCross,
          absoluteDot,
          symmetricalPerpendicularPoint,
          perpendicularPoint,
        } = getRelevantPoints({
          AAngle: rotation,
          AEnd: oldWall.end,
          AStart: oldWall.start,
          BEnd: newWall.end,
          AThickness: oldWall.thickness,
          BAngle: newWall.rotation,
          BStart: newWall.start,
          BThickness: newWall.thickness,
        })
        oldWall.beta = beta
        oldWall.dot = dotAB
        oldWall.cross = cross
        oldWall.absoluteCross = absoluteCross
        oldWall.absoluteDot = absoluteDot
        oldWall.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
        oldWall.perpendicularPoint = perpendicularPoint
        newWall.prevDot = dotAB
        newWall.prevCross = cross

        newWall.prevWall = id
        oldWall.nextWall = currentFirstWall
        // Clear area logic
        let height = -Infinity
        const newSides = []
        area.sides.forEach((wallID) => {
          newSides.push(wallID)
          const wall = store.items.get(wallID)
          if (!isWall(wall)) return
          if (wall.height > height) {
            height = wall.height
          }
        })
        if (!roof) return
        roof.sides = newSides
        roof.height = height
        roof.sides = newSides
        roof.isClosed = true
        roof.isRoof = true
        store.currentFirstWall = ''
        store.currentArea = ''
        store.currentRoof = ''
        store.currentObjectID = ''

        store.mode = 'MODE_IDLE'
      }
    })
  },
  editWallAngle: (id: string, angleInRadians: number) => {
    set((store) => {
      const wall = store.items.get(id)
      if (!isWall(wall)) return

      const prevWall = store.items.get(wall?.prevWall) as Walls
      const nextWall = store.items.get(wall?.nextWall) as Walls
      const nextnextWall = store.items.get(nextWall?.nextWall) as Walls
      if (!prevWall && !nextWall) {
        const rotation = angleInRadians % (2 * Math.PI)
        wall.rotation = rotation

        const width = wall.width
        const thickness = wall.thickness

        const [startX, startZ] = wall.start

        const direction = toVector3([Math.cos(rotation), 0, Math.sin(rotation)]).setLength(width)
        const newEnd = toVector3([startX, 0, startZ]).add(direction)
        const [centerX, centerZ] = calculateWallPosition([startX, startZ], [newEnd.x, newEnd.z])

        wall.x = centerX + (thickness / 2) * Math.sin(rotation)
        wall.z = centerZ - (thickness / 2) * Math.cos(rotation)
        wall.end = [newEnd.x, newEnd.z]
      } else if (!prevWall && nextWall) {
        const rotation = angleInRadians % (2 * Math.PI)

        const width = wall.width
        const thickness = wall.thickness

        const [startX, startZ] = wall.start

        const direction = toVector3([Math.cos(rotation), 0, Math.sin(rotation)]).setLength(width)
        const newEnd = toVector3([startX, 0, startZ]).add(direction)
        const [centerX, centerZ] = calculateWallPosition([startX, startZ], [newEnd.x, newEnd.z])

        wall.x = centerX + (thickness / 2) * Math.sin(rotation)
        wall.z = centerZ - (thickness / 2) * Math.cos(rotation)
        wall.end = [newEnd.x, newEnd.z]
        wall.rotation = rotation

        const {
          x,
          z,
          rotation: rotNext,
          width: widthNext,
        } = dragFromRightVertex(nextWall, nextWall.end[0], nextWall.end[1])

        nextWall.start = wall.end
        nextWall.x = x
        nextWall.z = z
        nextWall.rotation = rotNext
        nextWall.width = widthNext

        const { innerAngle } = getInnerAngle(wall.start, wall.end, nextWall.start, nextWall.end)
        nextWall.innerAngle = innerAngle

        const {
          x: x2,
          z: z2,
          rotation: rotNext2,
          width: widthNext2,
        } = dragFromRightVertex(nextWall, nextWall.end[0], nextWall.end[1])

        nextWall.start = wall.end
        nextWall.x = x2
        nextWall.z = z2
        nextWall.rotation = rotNext2
        nextWall.width = widthNext2

        const { innerAngle: innerAngle2 } = getInnerAngle(
          wall.start,
          wall.end,
          nextWall.start,
          nextWall.end,
        )
        nextWall.innerAngle = innerAngle2

        if (nextnextWall) {
          const {
            x: x3,
            z: z3,
            rotation: rotNext3,
            width: widthNext3,
            end,
          } = dragFromRightVertex(nextnextWall, nextnextWall.end[0], nextnextWall.end[1])

          nextnextWall.start = nextWall.end
          nextnextWall.x = x3
          nextnextWall.z = z3
          nextnextWall.rotation = rotNext3
          nextnextWall.width = widthNext3

          const { innerAngle: nextnextWallInnerAngle } = getInnerAngle(
            nextWall.start,
            nextWall.end,
            nextnextWall.start,
            nextnextWall.end,
          )
          nextnextWall.innerAngle = nextnextWallInnerAngle
        }
      } else if (prevWall && nextWall) {
        const prevRotation = prevWall.rotation || 0
        const angleDiff = Math.PI - angleInRadians
        const rotation = prevRotation + angleDiff
        wall.rotation = rotation

        const width = wall.width
        const thickness = wall.thickness

        const [startX, startZ] = wall.start

        const direction = toVector3([Math.cos(rotation), 0, Math.sin(rotation)]).setLength(width)
        const newEnd = toVector3([startX, 0, startZ]).add(direction)
        const [centerX, centerZ] = calculateWallPosition([startX, startZ], [newEnd.x, newEnd.z])

        wall.x = centerX + (thickness / 2) * Math.sin(rotation)
        wall.z = centerZ - (thickness / 2) * Math.cos(rotation)
        wall.end = [newEnd.x, newEnd.z]
        wall.innerAngle = angleInRadians

        const {
          x,
          z,
          rotation: rotNext,
          width: widthNext,
          end,
        } = dragFromRightVertex(nextWall, nextWall.end[0], nextWall.end[1])

        nextWall.start = wall.end
        nextWall.x = x
        nextWall.z = z
        nextWall.rotation = rotNext
        nextWall.width = widthNext
        nextWall.end = end

        const { innerAngle } = getInnerAngle(wall.start, wall.end, nextWall.start, nextWall.end)
        nextWall.innerAngle = innerAngle

        const {
          x: x2,
          z: z2,
          rotation: rotNext2,
          width: widthNext2,
          end: end2,
        } = dragFromRightVertex(nextWall, nextWall.end[0], nextWall.end[1])

        nextWall.start = wall.end
        nextWall.x = x2
        nextWall.z = z2
        nextWall.rotation = rotNext2
        nextWall.width = widthNext2
        nextWall.end = end2

        const { innerAngle: innerAngle2 } = getInnerAngle(
          wall.start,
          wall.end,
          nextWall.start,
          nextWall.end,
        )
        nextWall.innerAngle = innerAngle2

        if (nextnextWall) {
          const {
            x: x3,
            z: z3,
            rotation: rotNext3,
            width: widthEnd3,
            end: end3,
          } = dragFromRightVertex(nextnextWall, nextnextWall.end[0], nextnextWall.end[1])

          nextnextWall.start = nextWall.end
          nextnextWall.x = x3
          nextnextWall.z = z3
          nextnextWall.rotation = rotNext3
          nextnextWall.width = widthEnd3

          const { innerAngle: nextnextWallInnerAngle } = getInnerAngle(
            nextWall.start,
            nextWall.end,
            nextnextWall.start,
            nextnextWall.end,
          )
          nextnextWall.innerAngle = nextnextWallInnerAngle
        }
      } else {
        const prevRotation = prevWall.rotation || 0
        const angleDiff = Math.PI - angleInRadians
        const rotation = prevRotation + angleDiff
        wall.rotation = rotation

        const width = wall.width
        const thickness = wall.thickness

        const [startX, startZ] = wall.start

        const direction = toVector3([Math.cos(rotation), 0, Math.sin(rotation)]).setLength(width)
        const newEnd = toVector3([startX, 0, startZ]).add(direction)
        const [centerX, centerZ] = calculateWallPosition([startX, startZ], [newEnd.x, newEnd.z])

        wall.x = centerX + (thickness / 2) * Math.sin(rotation)
        wall.z = centerZ - (thickness / 2) * Math.cos(rotation)
        wall.end = [newEnd.x, newEnd.z]
        wall.innerAngle = angleInRadians
      }
    })
  },
  editWallAngleFromDirectionKey: (id: string, rotation: number) => {
    set((store) => {
      const wall = store.items.get(id)
      if (!isWall(wall)) return

      const prevWall = store.items.get(wall?.prevWall)

      const width = wall.width
      const thickness = wall.thickness
      const [startX, startZ] = wall.start

      const direction = toVector3([Math.cos(rotation), 0, Math.sin(rotation)]).setLength(width)
      const newEnd = toVector3([startX, 0, startZ]).add(direction)
      const [centerX, centerZ] = calculateWallPosition([startX, startZ], [newEnd.x, newEnd.z])

      if (rotation !== oneEightyDeg) {
        wall.x = centerX + (thickness / 2) * Math.sin(rotation)
        wall.z = centerZ - (thickness / 2) * Math.cos(rotation)
        wall.end = [newEnd.x, newEnd.z]
      } else {
        wall.x = centerX - (thickness / 2) * Math.cos(rotation)
        wall.z = centerZ + (thickness / 2) * Math.sin(rotation)
        wall.end = [newEnd.x, newEnd.z]
      }

      wall.rotation = rotation

      if (isWall(prevWall)) {
        const {
          beta,
          dotAB,
          cross,
          absoluteCross,
          absoluteDot,
          symmetricalPerpendicularPoint,
          perpendicularPoint,
        } = getRelevantPoints({
          AAngle: prevWall.rotation,
          AEnd: prevWall.end,
          AStart: prevWall.start,
          BEnd: wall.end,
          AThickness: prevWall.thickness,
          BAngle: rotation,
          BStart: wall.start,
          BThickness: wall.thickness,
        })
        wall.beta = beta
        wall.dot = dotAB
        wall.cross = cross
        wall.absoluteCross = absoluteCross
        wall.absoluteDot = absoluteDot
        wall.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
        wall.perpendicularPoint = perpendicularPoint

        const { innerAngle } = getInnerAngle(prevWall.start, prevWall.end, wall.start, wall.end)
        wall.innerAngle = innerAngle
      }
    })
  },
  editWallThicknessDrawingMode: (newThickness: number) => {
    set((store) => {
      store.lastThickness = newThickness

      store.items.forEach((wall) => {
        if (!isWall(wall)) return
        wall.thickness = newThickness
      })
    })
  },
  editWallProperty: <T extends Walls>(
    id: string,
    config?: Partial<Record<keyof T, T[keyof T]>>,
  ) => {
    set((store) => {
      if (!config) return

      // Verificar si es una actualización SOLO de thickness
      const isOnlyThicknessUpdate =
        Object.keys(config).length === 1 && 'thickness' in config && config.thickness !== undefined

      if (isOnlyThicknessUpdate) {
        const newThickness = config.thickness as number

        let firstWallId = ''
        let lastWallId = ''

        store.items.forEach((wall, wallId) => {
          if (!isWall(wall)) return

          const oldThickness = wall.thickness
          const rotation = wall.rotation

          wall.thickness = newThickness

          const thicknessOffset = (newThickness - oldThickness) / 2
          wall.x += thicknessOffset * Math.sin(rotation)
          wall.z -= thicknessOffset * Math.cos(rotation)

          if (!wall.prevWall) firstWallId = wallId
          if (!wall.nextWall) lastWallId = wallId
        })

        const firstWall = store.items.get(firstWallId) as Walls
        const lastWall = store.items.get(lastWallId) as Walls

        if (firstWall && lastWall) {
          lastWall.end = firstWall.start
          lastWall.width = Math.hypot(
            lastWall.end[0] - lastWall.start[0],
            lastWall.end[1] - firstWall.start[1],
          )

          firstWall.prevWall = lastWallId
          lastWall.nextWall = firstWallId

          store.items.forEach((wall) => {
            if (!isWall(wall) || !wall.prevWall) return

            const prevWall = store.items.get(wall.prevWall) as Walls
            if (!prevWall) return

            const {
              dotAB,
              cross,
              absoluteCross,
              absoluteDot,
              symmetricalPerpendicularPoint,
              perpendicularPoint,
            } = getRelevantPoints({
              AAngle: prevWall.rotation,
              AEnd: prevWall.end,
              AStart: prevWall.start,
              BEnd: wall.end,
              AThickness: prevWall.thickness,
              BAngle: wall.rotation,
              BStart: wall.start,
              BThickness: wall.thickness,
            })

            wall.prevDot = dotAB
            wall.prevCross = cross
            prevWall.absoluteCross = absoluteCross
            prevWall.absoluteDot = absoluteDot
            prevWall.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
            prevWall.perpendicularPoint = perpendicularPoint
          })
        }

        const area = store.areas.get(store.currentArea)
        if (
          area &&
          firstWall &&
          lastWall &&
          lastWall.end[0] === firstWall.start[0] &&
          lastWall.end[1] === firstWall.start[1]
        ) {
          closeFloorAndRoof(store)
        }
      } else {
        // Lógica normal para otras propiedades dibujado libre
        const wall = store.items.get(id)
        if (!isWall(wall)) return

        for (const [property, value] of Object.entries(config)) {
          wall[property] = value
        }
      }
    })
  },
  editWallTexture: <T extends Walls>(id: string, config?: Partial<Record<keyof T, T[keyof T]>>) => {
    if (config) {
      set((store) => {
        for (const [property, value] of Object.entries(config)) {
          const item = store.items.get(id) as Walls
          if (!item) continue

          item.textures.forEach((prop, propIndex) => {
            if (property === prop.id) {
              // Sobreescribo las llaves del objeto por aquellas incluidas en el config e incluyo sin tocar las llaves no modificadas
              const prevData = item.textures[propIndex]
              item.textures[propIndex] = { ...prevData, ...value }
            }
          })
        }
      })
    }
  },
  deleteAllWalls: () => {
    set((store) => {
      const currLayer = store.layers[store.currentLayer]
      if (!currLayer) return

      currLayer.items.forEach((item: string) => {
        const object = store.items.get(item)

        if (object && object?.type === 'walls') {
          store.sceneBoundingBoxes.delete(item)
          store.sceneBoundingBoxes.delete(getWallCOBB(item))
          object.holes.forEach((hole) => {
            store.items.delete(hole)
            store.layers[store.currentLayer].items.delete(hole)
          })
          store.layers[store.currentLayer].items.delete(item)
          store.items.delete(item)
        }
      })
      currLayer.areas.forEach((areaID) => {
        store.areas.delete(areaID)
        store.layers[store.currentLayer].items.delete(areaID)
      })
      currLayer.areas = new Set()
      store.items.delete('lights_baseSceneLight')
    })
  },
  editWallFromCenter: (xE: number, zE: number) => {
    const vertexBeingDragged = get().vertexBeingDragged
    if (!vertexBeingDragged) return

    const [id, type, prevWallId = '', nextWallId = ''] = vertexBeingDragged
    if (type !== 'centerVertex') return

    const items = get().items
    const item = items.get(id)
    if (!isWall(item)) return

    const [startX, startZ] = item.start
    const [endX, endZ] = item.end
    const centerX = (startX + endX) / 2
    const centerZ = (startZ + endZ) / 2

    // Mantener la pared paralela: se desplaza en el eje perpendicular.
    const isHorizontalWall = Math.abs(endX - startX) >= Math.abs(endZ - startZ)
    const delta = isHorizontalWall ? zE - centerZ : xE - centerX
    if (Math.abs(delta) < 0.0001) return

    const newStart: [number, number] = isHorizontalWall
      ? [startX, startZ + delta]
      : [startX + delta, startZ]
    const newEnd: [number, number] = isHorizontalWall ? [endX, endZ + delta] : [endX + delta, endZ]

    const updateHole = (wallId: string) => get().updateWallHoles(wallId)
    const mergeWalls = get().mergeWalls
    const addInnerAngle = get().addInnerAngle
    const updateArea = get().updateArea

    set((store) => {
      const current = store.items.get(id)
      if (!isWall(current)) return

      const dist2 = (a: [number, number], b: [number, number]) => {
        const dx = a[0] - b[0]
        const dz = a[1] - b[1]
        return dx * dx + dz * dz
      }
      const preservePiSign = (oldRotation: number, newRotation: number) => {
        const EPS = 1e-6
        const oldIsPi = Math.abs(Math.abs(oldRotation) - Math.PI) < EPS
        const newIsPi = Math.abs(Math.abs(newRotation) - Math.PI) < EPS
        if (oldIsPi && newIsPi) {
          return oldRotation >= 0 ? Math.PI : -Math.PI
        }
        return newRotation
      }

      // 1) Mueve pared actual paralela a sí misma
      current.start = newStart
      current.end = newEnd
      current.x = isHorizontalWall ? current.x : current.x + delta
      current.z = isHorizontalWall ? current.z + delta : current.z

      // 2) Ajusta adyacente previa en el extremo compartido
      if (prevWallId) {
        const prev = store.items.get(prevWallId)
        if (isWall(prev)) {
          const prevOldRotation = prev.rotation
          const sharedIsPrevEnd = dist2(prev.end, newStart) <= dist2(prev.start, newStart)
          if (sharedIsPrevEnd) {
            const { x, z, rotation, width } = dragFromRightVertex(prev, newStart[0], newStart[1])
            prev.x = x
            prev.z = z
            prev.rotation = preservePiSign(prevOldRotation, rotation)
            prev.width = width
            prev.end = newStart
          } else {
            const { x, z, rotation, width, start } = dragFromLeftVertex(prev, newStart[0], newStart[1])
            prev.x = x
            prev.z = z
            prev.rotation = preservePiSign(prevOldRotation, rotation)
            prev.width = width
            prev.start = start
          }
        }
      }

      // 3) Ajusta adyacente siguiente en el extremo compartido
      if (nextWallId) {
        const next = store.items.get(nextWallId)
        if (isWall(next)) {
          const nextOldRotation = next.rotation
          const sharedIsNextStart = dist2(next.start, newEnd) <= dist2(next.end, newEnd)
          if (sharedIsNextStart) {
            const { x, z, rotation, width } = dragFromLeftVertex(next, newEnd[0], newEnd[1])
            next.x = x
            next.z = z
            next.rotation = preservePiSign(nextOldRotation, rotation)
            next.width = width
            next.start = newEnd
          } else {
            const { x, z, rotation, width, end } = dragFromRightVertex(next, newEnd[0], newEnd[1])
            next.x = x
            next.z = z
            next.rotation = preservePiSign(nextOldRotation, rotation)
            next.width = width
            next.end = end
          }
        }
      }
    })

    // Recalcular solo paredes locales
    updateHole(id)
    if (prevWallId) updateHole(prevWallId)
    if (nextWallId) updateHole(nextWallId)
    if (prevWallId) {
      mergeWalls(id)
      addInnerAngle(prevWallId, id)
    }
    if (nextWallId) {
      mergeWalls(nextWallId)
      addInnerAngle(id, nextWallId)
    }

    // Refresca solo datos de unión/ángulo en las esquinas opuestas adyacentes
    // (no mueve geometría), para evitar quiebres visuales en next->nextWall.
    const itemsForJoins = get().items
    const prevWallItem = prevWallId ? itemsForJoins.get(prevWallId) : null
    const nextWallItem = nextWallId ? itemsForJoins.get(nextWallId) : null

    if (isWall(prevWallItem) && prevWallItem.prevWall) {
      mergeWalls(prevWallId)
      addInnerAngle(prevWallItem.prevWall, prevWallId)
    }
    if (isWall(nextWallItem) && nextWallItem.nextWall) {
      mergeWalls(nextWallItem.nextWall)
      addInnerAngle(nextWallId, nextWallItem.nextWall)
    }

    updateArea(id)
    if (prevWallId) updateArea(prevWallId)
    if (nextWallId) updateArea(nextWallId)
  },
  moveWallParallelByDelta: (id: string, delta: number) => {
    if (!Number.isFinite(delta) || Math.abs(delta) < 0.0001) return
    const wall = get().items.get(id)
    if (!isWall(wall)) return

    const [startX, startZ] = wall.start
    const [endX, endZ] = wall.end
    const centerX = (startX + endX) / 2
    const centerZ = (startZ + endZ) / 2
    const isHorizontalWall = Math.abs(endX - startX) >= Math.abs(endZ - startZ)

    const xE = isHorizontalWall ? centerX : centerX + delta
    const zE = isHorizontalWall ? centerZ + delta : centerZ

    const previousVertex = get().vertexBeingDragged
    get().setBeingDrawnVertex([id, 'centerVertex', wall.prevWall ?? '', wall.nextWall ?? ''])
    get().editWallFromCenter(xE, zE)
    get().setBeingDrawnVertex(previousVertex ?? ['', ''])
  },
  editWallFromVertex: (xE: number, zE: number) => {
    if (get().vertexBeingDragged?.[1] === 'centerVertex') {
      get().editWallFromCenter(xE, zE)
      return
    }
    //If editting starts on leftVertex it means you are using wall.end as a pivot.
    const updateHole = (id: string) => get().updateWallHoles(id)
    set((store) => {
      if (!store.vertexBeingDragged) return
      const [id, type, prevWallIfLeftnextWallIfRight, nextWallIfCenter] = store.vertexBeingDragged
      const item = store.items.get(id)

      if (!isWall(item)) return
      if (type === 'centerVertex') {
        const [startX, startZ] = item.start
        const [endX, endZ] = item.end
        const centerX = (startX + endX) / 2
        const centerZ = (startZ + endZ) / 2

        // Si la pared corre en X, arrastramos por Z. Si corre en Z, arrastramos por X.
        const isHorizontalWall = Math.abs(endX - startX) >= Math.abs(endZ - startZ)
        const delta = isHorizontalWall ? zE - centerZ : xE - centerX

        if (delta === 0) return

        const newStart: [number, number] = isHorizontalWall
          ? [startX, startZ + delta]
          : [startX + delta, startZ]
        const newEnd: [number, number] = isHorizontalWall
          ? [endX, endZ + delta]
          : [endX + delta, endZ]

        item.start = newStart
        item.end = newEnd
        item.x = isHorizontalWall ? item.x : item.x + delta
        item.z = isHorizontalWall ? item.z + delta : item.z
        updateHole(id)

        const prevWallId = prevWallIfLeftnextWallIfRight
        const nextWallId = nextWallIfCenter

        if (prevWallId) {
          const prevItem = store.items.get(prevWallId)
          if (isWall(prevItem)) {
            const { x, z, rotation, width } = dragFromRightVertex(prevItem, newStart[0], newStart[1])
            prevItem.x = x
            prevItem.z = z
            prevItem.rotation = rotation
            prevItem.width = width
            prevItem.end = newStart
            updateHole(prevWallId)
          }
        }

        if (nextWallId) {
          const nextItem = store.items.get(nextWallId)
          if (isWall(nextItem)) {
            const { x, z, rotation, width } = dragFromLeftVertex(nextItem, newEnd[0], newEnd[1])
            nextItem.x = x
            nextItem.z = z
            nextItem.rotation = rotation
            nextItem.width = width
            nextItem.start = newEnd
            updateHole(nextWallId)
          }
        }

        const mergeWalls = get().mergeWalls
        if (item.prevWall) mergeWalls(id)
        if (item.nextWall) mergeWalls(item.nextWall)

        const addInnerAngle = get().addInnerAngle
        if (item.prevWall) addInnerAngle(item.prevWall, id)
        if (item.nextWall) addInnerAngle(id, item.nextWall)

        const updateArea = get().updateArea
        updateArea(id)
        if (prevWallId) updateArea(prevWallId)
        if (nextWallId) updateArea(nextWallId)

        return
      }
      if (type === 'leftVertex') {
        if (!item) return
        const { x, z, rotation, width, start } = dragFromLeftVertex(item, xE, zE)
        item.x = x
        item.z = z
        item.rotation = rotation
        item.width = width
        item.start = start
        updateHole(id)
        if (prevWallIfLeftnextWallIfRight) {
          const prevItem = store.items.get(prevWallIfLeftnextWallIfRight) as Walls
          if (!isWall(prevItem)) return
          const {
            x: x2,
            z: z2,
            rotation: rot2,
            width: w2,
            end: e2,
          } = dragFromRightVertex(prevItem, xE, zE)

          prevItem.x = x2
          prevItem.z = z2
          prevItem.rotation = rot2
          prevItem.width = w2
          prevItem.end = start
          //Checkear la walla
          //Cases: Ambas positivas: derecha arriba ambas
          //Positiva negativa z:
          const itemProps = getInnerAngle(prevItem.start, prevItem.end, start, item.end)
          item.innerAngle = itemProps.innerAngle
          const {
            beta,
            perpendicularPoint,
            symmetricalPerpendicularPoint,
            absoluteCross,
            absoluteDot,
            cross,
            dotAB,
          } = getRelevantPoints({
            //prevWall es A
            AAngle: rot2,
            AStart: prevItem.start,
            AEnd: start,
            AThickness: prevItem.thickness,
            //item es B
            BAngle: rotation,
            BStart: start,
            BEnd: item.end,
            BThickness: item.thickness,
          })
          prevItem.beta = beta
          prevItem.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
          prevItem.perpendicularPoint = perpendicularPoint
          prevItem.dot = dotAB
          prevItem.cross = cross
          prevItem.absoluteCross = absoluteCross
          prevItem.absoluteDot = absoluteDot
          item.prevDot = dotAB
          item.prevCross = cross
          // item-> / _ <- prevItem ya que dragueas desde el start
          const nextWall = item.nextWall
          const prevprevWall = prevItem.prevWall
          updateHole(prevprevWall)
          updateHole(prevWallIfLeftnextWallIfRight)

          if (nextWall) {
            const nextItem = store.items.get(nextWall)! as Walls
            const { innerAngle } = getInnerAngle(item.start, item.end, nextItem.start, nextItem.end)
            nextItem.innerAngle = innerAngle
            const {
              beta,
              perpendicularPoint,
              symmetricalPerpendicularPoint,
              absoluteCross,
              absoluteDot,
              cross,
              dotAB,
            } = getRelevantPoints({
              //item es A
              AAngle: rotation,
              AStart: start,
              AEnd: item.end,
              AThickness: item.thickness,
              //nextItem es B
              BAngle: nextItem.rotation,
              BStart: nextItem.start,
              BEnd: nextItem.end,
              BThickness: nextItem.thickness,
            })
            item.beta = beta
            item.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
            item.perpendicularPoint = perpendicularPoint
            item.dot = dotAB
            item.cross = cross
            item.absoluteCross = absoluteCross
            item.absoluteDot = absoluteDot
            nextItem.prevDot = dotAB
            nextItem.prevCross = cross
            updateHole(nextWall)
          }
          if (prevprevWall) {
            const prevPrevItem = store.items.get(prevprevWall)! as Walls
            const prev = getInnerAngle(
              prevPrevItem.start,
              prevPrevItem.end,
              prevItem.start,
              prevItem.end,
            )
            const {
              beta,
              perpendicularPoint,
              symmetricalPerpendicularPoint,
              absoluteCross,
              absoluteDot,
              cross,
              dotAB,
            } = getRelevantPoints({
              //prevprevWall es A
              AAngle: prevPrevItem.rotation,
              AStart: prevPrevItem.start,
              AEnd: prevPrevItem.end,
              AThickness: prevPrevItem.thickness,
              //prevWall es B
              BAngle: rot2,
              BStart: prevItem.start,
              BEnd: start,
              BThickness: prevItem.thickness,
            })
            prevPrevItem.beta = beta
            prevPrevItem.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
            prevPrevItem.perpendicularPoint = perpendicularPoint
            prevPrevItem.dot = dotAB
            prevPrevItem.cross = cross
            prevPrevItem.absoluteCross = absoluteCross
            prevPrevItem.absoluteDot = absoluteDot
            prevItem.prevDot = dotAB
            prevItem.prevCross = cross
            prevItem.innerAngle = prev.innerAngle
            updateHole(prevprevWall)
          }
          // walla 2-> walla1>
          // ->prevWall/   \ <- item
          // Actualizar el innerAngle de prevItem Y prevItemnextWall
        }

        return
      } else {
        // Right verte
        if (!item) return
        const { x, z, rotation, width, end } = dragFromRightVertex(item, xE, zE)
        item.x = x
        item.z = z
        item.rotation = rotation
        item.width = width
        item.end = end
        updateHole(id)
        if (prevWallIfLeftnextWallIfRight) {
          const nextItem = store.items.get(prevWallIfLeftnextWallIfRight) as Walls
          const { x: x2, z: z2, rotation: rot2, width: w2 } = dragFromLeftVertex(nextItem, xE, zE)

          nextItem.x = x2
          nextItem.z = z2
          nextItem.rotation = rot2
          nextItem.width = w2
          nextItem.start = end
          const { innerAngle } = getInnerAngle(item.start, end, end, nextItem.end) //Este es el ángulo de rotación
          updateHole(prevWallIfLeftnextWallIfRight)
          const {
            beta,
            perpendicularPoint,
            symmetricalPerpendicularPoint,
            absoluteCross,
            absoluteDot,
            cross,
            dotAB,
          } = getRelevantPoints({
            //item es A
            AAngle: rotation,
            AStart: item.start,
            AEnd: end,
            AThickness: item.thickness,
            //nextItem es B
            BAngle: rot2,
            BStart: end,
            BEnd: nextItem.end,
            BThickness: nextItem.thickness,
          })
          item.beta = beta
          item.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
          item.perpendicularPoint = perpendicularPoint
          item.dot = dotAB
          item.cross = cross
          item.absoluteCross = absoluteCross
          item.absoluteDot = absoluteDot
          nextItem.prevDot = dotAB
          nextItem.prevCross = cross
          nextItem.innerAngle = innerAngle
          const nextnextWall = nextItem.nextWall
          const prevWall = item.prevWall
          if (nextnextWall) {
            const nextNextItem = store.items.get(nextnextWall)! as Walls
            const nextItemProps = getInnerAngle(
              end,
              nextItem.end,
              nextNextItem.start,
              nextNextItem.end,
            )
            const {
              beta,
              perpendicularPoint,
              symmetricalPerpendicularPoint,
              absoluteCross,
              absoluteDot,
              cross,
              dotAB,
            } = getRelevantPoints({
              //nextItem es A
              AAngle: rot2,
              AStart: end,
              AEnd: nextItem.end,
              AThickness: nextItem.thickness,
              //nextNextItem es B
              BAngle: nextNextItem.rotation,
              BStart: nextNextItem.start,
              BEnd: nextNextItem.end,
              BThickness: nextNextItem.thickness,
            })
            nextItem.beta = beta
            nextItem.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
            nextItem.perpendicularPoint = perpendicularPoint
            nextItem.dot = dotAB
            nextItem.cross = cross
            nextItem.absoluteCross = absoluteCross
            nextItem.absoluteDot = absoluteDot
            nextNextItem.prevDot = dotAB
            nextNextItem.prevCross = cross
            nextNextItem.innerAngle = nextItemProps.innerAngle
            updateHole(nextnextWall)
          }

          // ->item /   \ <- prevItem.
          // Actualizar el innerAngle de prevItem Y prevItemnextWall

          if (prevWall) {
            const prevItem = store.items.get(prevWall)! as Walls
            const itemProps = getInnerAngle(prevItem.start, item.start, item.start, end)
            const {
              beta,
              perpendicularPoint,
              symmetricalPerpendicularPoint,
              absoluteCross,
              absoluteDot,
              cross,
              dotAB,
            } = getRelevantPoints({
              //prevItem es A
              AAngle: prevItem.rotation,
              AStart: prevItem.start,
              AEnd: prevItem.end,
              AThickness: prevItem.thickness,
              //item es B
              BAngle: rotation,
              BStart: item.start,
              BEnd: end,
              BThickness: item.thickness,
            })
            prevItem.beta = beta
            prevItem.symmetricalPerpendicularPoint = symmetricalPerpendicularPoint
            prevItem.perpendicularPoint = perpendicularPoint
            prevItem.dot = dotAB
            prevItem.cross = cross
            prevItem.absoluteCross = absoluteCross
            prevItem.absoluteDot = absoluteDot
            item.prevDot = dotAB
            item.prevCross = cross
            updateHole(prevWall)
            item.innerAngle = itemProps.innerAngle
          }
        }
        return
      }
    })
  },
  editWallParameter: <T extends Walls>(id: string, obj: Record<keyof T, T[keyof T]>) => {
    const updateWallHoles = () => get().updateWallHoles(id)
    set((store) => {
      const item = store.items.get(id) as Walls
      if (!item) return console.error(`Item not found with id: ${id}`)
      Object.entries(obj).forEach(([key, value]) => {
        item[key] = value
      })
    })

    updateWallHoles()
  },

  drawSquareRoom: (
    sizesA: Record<string, number>,
    sizesB: Record<string, number>,
    height: number,
  ) => {
    const drawLight = (
      id: string,
      icon: string,
      catalogID: string,
      height?: number,
      matrix?: Matrix4,
    ) => get().drawLight(id, icon, catalogID, false, height, matrix)
    set((store) => {
      const [wall1id, wall2id, wall3id, wall4id] = [
        getId('walls'),
        getId('walls'),
        getId('walls'),
        getId('walls'),
      ]
      const areaId = getId('areas')
      const areaId2 = getId('areas')

      const wa = sizesA.width / 2
      const ta = sizesA.thickness / 2

      const wb = sizesB.width / 2
      const tb = sizesB.thickness / 2
      const a1Start = [wb, -wa] satisfies [number, number]
      const a1End = [wb, wa] satisfies [number, number]

      const b1Start = [wb, wa] satisfies [number, number]
      const b1End = [-wb, wa] satisfies [number, number]

      const a2Start = [-wb, wa] satisfies [number, number]
      const a2End = [-wb, -wa] satisfies [number, number]

      const b2Start = [-wb, -wa] satisfies [number, number]
      const b2End = [wb, -wa] satisfies [number, number]
      const {
        beta,
        absoluteCross,
        absoluteDot,
        dotAB,
        cross,
        symmetricalPerpendicularPoint,
        perpendicularPoint,
      } = getRelevantPoints({
        AStart: a1Start,
        AEnd: a1End,
        BEnd: b1End,
        BStart: b1Start,
        AThickness: ta,
        BThickness: tb,
        AAngle: ninetyDeg,
        BAngle: oneEightyDeg,
      })
      const {
        beta: beta2,
        absoluteCross: ac2,
        absoluteDot: ad2,
        dotAB: d2,
        cross: c2,
        symmetricalPerpendicularPoint: spp2,
        perpendicularPoint: pp2,
      } = getRelevantPoints({
        AStart: b1Start,
        AEnd: b1End,
        BEnd: a2End,
        BStart: a2Start,
        AThickness: ta,
        BThickness: tb,
        AAngle: oneEightyDeg,
        BAngle: -ninetyDeg,
      })
      const {
        beta: beta3,
        absoluteCross: ac3,
        absoluteDot: ad3,
        dotAB: d3,
        cross: c3,
        symmetricalPerpendicularPoint: spp3,
        perpendicularPoint: pp3,
      } = getRelevantPoints({
        AStart: a2Start,
        AEnd: a2End,
        BEnd: b2End,
        BStart: b2Start,
        AThickness: ta,
        BThickness: tb,
        AAngle: -ninetyDeg,
        BAngle: 0,
      })
      const {
        beta: beta4,
        absoluteCross: ac4,
        absoluteDot: ad4,
        dotAB: d4,
        cross: c4,
        symmetricalPerpendicularPoint: spp4,
        perpendicularPoint: pp4,
      } = getRelevantPoints({
        AStart: b2Start,
        AEnd: b2End,
        BEnd: a1End,
        BStart: a1Start,
        AThickness: ta,
        BThickness: tb,
        AAngle: 0,
        BAngle: ninetyDeg,
      })
      const increment = store.increment
      store.items.set(
        wall1id,
        getBaseWall(
          {
            prevWall: wall4id,
            nextWall: wall2id,
            areas: new Set([areaId, areaId2]),
            thickness: sizesA.thickness,
            rotation: ninetyDeg,
            x: wb + ta,
            z: 0,
            id: wall1id,
            start: a1Start,
            end: a1End,
            height,
            width: sizesA.width,
            innerAngle: ninetyDeg,
            beta,
            absoluteCross,
            absoluteDot,
            cross,
            dot: dotAB,
            symmetricalPerpendicularPoint,
            perpendicularPoint,
            prevDot: d4,
            increment: increment,
          },
          store,
        ),
      )
      store.items.set(
        wall2id,
        getBaseWall(
          {
            prevWall: wall1id,
            nextWall: wall3id,
            areas: new Set([areaId, areaId2]),
            thickness: sizesB.thickness,
            height,
            start: b1Start,
            end: b1End,
            rotation: oneEightyDeg,
            z: wa + tb,
            x: 0,
            id: wall2id,
            width: sizesB.width,
            innerAngle: ninetyDeg,
            prevDot: dotAB,
            prevCross: cross,
            dot: d2,
            beta: beta2,
            absoluteCross: ac2,
            absoluteDot: ad2,
            cross: c2,
            symmetricalPerpendicularPoint: spp2,
            perpendicularPoint: pp2,
            increment: increment + 1,
          },
          store,
        ),
      )
      store.items.set(
        wall3id,
        getBaseWall(
          {
            prevWall: wall2id,
            nextWall: wall4id,
            areas: new Set([areaId, areaId2]),
            thickness: sizesA.thickness,
            height,
            start: a2Start,
            end: a2End,
            rotation: -ninetyDeg,
            width: sizesA.width,
            id: wall3id,
            x: -wb - ta,
            z: 0,
            innerAngle: ninetyDeg,
            beta: beta3,
            absoluteCross: ac3,
            absoluteDot: ad3,
            dot: d3,
            cross: c3,
            symmetricalPerpendicularPoint: spp3,
            perpendicularPoint: pp3,
            prevDot: d2,
            increment: increment + 2,
          },
          store,
        ),
      )
      store.items.set(
        wall4id,
        getBaseWall(
          {
            prevWall: wall3id,
            nextWall: wall1id,
            areas: new Set([areaId, areaId2]),
            thickness: sizesB.thickness,
            height,
            rotation: 0,
            start: b2Start,
            end: b2End,
            z: -wa - tb,
            id: wall4id,
            width: sizesB.width,
            x: 0,
            innerAngle: ninetyDeg,
            beta: beta4,
            absoluteCross: ac4,
            absoluteDot: ad4,
            dot: d4,
            cross: c4,
            symmetricalPerpendicularPoint: spp4,
            perpendicularPoint: pp4,
            prevDot: d3,
            increment: increment + 3,
          },
          store,
        ),
      )
      store.areas.set(
        areaId,
        getBaseArea({
          id: areaId,
          sides: [wall1id, wall2id, wall3id, wall4id],
          isClosed: true,
          increment: increment + 4,
        }),
      )
      store.areas.set(
        areaId2,
        getBaseArea({
          id: areaId2,
          isRoof: true,
          sides: [wall1id, wall2id, wall3id, wall4id],
          isClosed: true,
          height,
          increment: increment + 5,
        }),
      )
      if (
        !store.items.has('lights_baseSceneLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseSceneLight')
      ) {
        drawLight(
          'lights_baseSceneLight',
          '',
          'LUZGENERAL',
          height,
          // new Matrix4()
          //   .setPosition(toVector3([0, 0, 40]))
          //   .makeRotationFromQuaternion(new Quaternion(0, 0, 0, 1)),
        )
      }
      // const lightMatrix2 = new Matrix4()
      //   .clone()
      //   .setPosition(toVector3([40, 0, 0]))
      //   .makeRotationFromQuaternion(new Quaternion(0, 0, 0, 1))
      if (
        !store.items.has('lights_baseAreaLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseAreaLight')
      ) {
        drawLight('lights_baseAreaLight', '', 'Luzgeneral2', height)
      } else {
        store.items.delete('lights_baseAreaLight')
        drawLight('lights_baseAreaLight', '', 'Luzgeneral2', height)
      }
      store.layers[store.currentLayer].items.add(wall1id).add(wall2id).add(wall3id).add(wall4id)
      store.layers[store.currentLayer].areas.add(areaId).add(areaId2)
      store.cameraCenter = [0, 3000, 0]
      store.increment = increment + 6
      hydrateClosedRoomAreas(store)
      setTimeout(() => setResource('Walls', true), 3000)
    })
  },
  drawStraightRoom: (sizesA: Record<string, number>, height: number) => {
    const drawLight = (
      id: string,
      icon: string,
      catalogID: string,
      height?: number,
      matrix?: Matrix4,
    ) => get().drawLight(id, icon, catalogID, false, height, matrix)

    set((store) => {
      const [wall1id, wall2id, wall3id, wall4id] = [
        getId('walls'),
        getId('walls'),
        getId('walls'),
        getId('walls'),
      ]
      const areaId = getId('areas')
      const areaId2 = getId('areas')
      const wa = sizesA.width / 2
      const ta = sizesA.thickness / 2
      const a1Start = [wa, -wa] satisfies [number, number]
      const a1End = [wa, wa] satisfies [number, number]
      const b1Start = [wa, wa] satisfies [number, number]
      const a2Start = [-wa, wa] satisfies [number, number]
      const b2Start = [-wa, -wa] satisfies [number, number]

      store.items.set(
        wall1id,
        getBaseWall(
          {
            showAngle: false,
            visible: true,
            prevWall: wall4id,
            nextWall: wall2id,
            areas: new Set([areaId]),
            thickness: sizesA.thickness,
            rotation: ninetyDeg,
            x: wa + ta,
            z: 0,
            id: wall1id,
            start: a1Start,
            end: a1End,
            height,
            width: sizesA.width,
          },
          store,
        ),
      )
      store.items.set(
        wall2id,
        getBaseWall(
          {
            showAngle: false,
            visible: false,
            prevWall: wall1id,
            nextWall: wall3id,
            areas: new Set([areaId]),
            thickness: 0.1,
            height: 1,
            start: b1Start,
            end: a1End.map((el) => el + 0.1),
            rotation: oneEightyDeg,
            z: wa + ta,
            // x: 0,
            y: 9000,
            id: wall2id,
            // width: 0.1,
          },
          store,
        ),
      )

      store.items.set(
        wall3id,
        getBaseWall(
          {
            showAngle: false,
            visible: false,
            prevWall: wall2id,
            nextWall: wall4id,
            areas: new Set([areaId]),
            thickness: 0.1,
            // height: 1,
            height,
            start: a2Start,
            end: a2Start.map((el) => el + 0.1),
            rotation: -ninetyDeg,
            // width: 0.1,
            id: wall3id,
            x: -wa - ta,
            // z: 0,
          },
          store,
        ),
      )
      store.items.set(
        wall4id,
        getBaseWall(
          {
            showAngle: false,
            visible: false,
            prevWall: wall3id,
            nextWall: wall1id,
            areas: new Set([areaId]),
            thickness: 0.1,
            // height: 1,
            height,
            start: b2Start,
            end: b2Start.map((el) => el + 0.1),
            z: -wa - ta,
            id: wall4id,
            y: 9000,
            // width: 0.1,
            isRoof: true,
            sides: [wall1id, wall2id, wall3id, wall4id],
            isClosed: true,
            // height,
          },
          store,
        ),
      )

      if (
        !store.items.has('lights_baseSceneLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseSceneLight')
      ) {
        drawLight('lights_baseSceneLight', '', 'LUZGENERAL', height)
      }
      // const lightMatrix2 = new Matrix4()
      //   .clone()
      //   .setPosition(toVector3([20, 0, -20]))
      //   .makeRotationFromQuaternion(new Quaternion(0, 0, 0, 1))

      if (
        !store.items.has('lights_baseAreaLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseAreaLight')
      ) {
        drawLight('lights_baseAreaLight', '', 'Luzgeneral2', height)
      } else {
        const base = store.items.get('lights_baseAreaLight')
        if (isLight(base)) {
          // base.matrix = lightMatrix2
        }
      }
      store.areas.set(
        areaId,
        getBaseArea({
          id: areaId,
          sides: [wall1id, wall2id, wall3id, wall4id],
          isClosed: true,
        }),
      )
      store.areas.set(
        areaId2,
        getBaseArea({
          id: areaId2,
          isRoof: true,
          sides: [wall1id, wall2id, wall3id, wall4id],
          isClosed: true,
          height,
        }),
      )

      store.layers[store.currentLayer].items.add(wall1id).add(wall2id).add(wall3id).add(wall4id)
      store.layers[store.currentLayer].areas.add(areaId).add(areaId2)
      store.cameraCenter = [0, 4000, 0]
    })
  },
  drawLRoom: (sizesA: Record<string, number>, sizesB: Record<string, number>, height: number) => {
    const drawLight = (
      id: string,
      icon: string,
      catalogID: string,
      height?: number,
      matrix?: Matrix4,
    ) => get().drawLight(id, icon, catalogID, false, height, matrix)

    set((store) => {
      const [wall1id, wall2id, wall3id, wall4id] = [
        getId('walls'),
        getId('walls'),
        getId('walls'),
        getId('walls'),
      ]
      const areaId = getId('areas')
      const areaId2 = getId('areas')

      const wa = sizesA.width / 2
      const ta = sizesA.thickness / 2

      const wb = sizesB.width / 2
      const tb = sizesB.thickness / 2
      const a1Start = [wb, -wa] satisfies [number, number]
      const a1End = [wb, wa] satisfies [number, number]

      const b1Start = [wb, wa] satisfies [number, number]
      const b1End = [-wb, wa] satisfies [number, number]

      const a2Start = [-wb, wa] satisfies [number, number]
      const a2End = [-wb, -wa] satisfies [number, number]

      const b2Start = [-wb, -wa] satisfies [number, number]
      const b2End = [wb, -wa] satisfies [number, number]

      const {
        beta,
        absoluteCross,
        absoluteDot,
        dotAB,
        cross,
        symmetricalPerpendicularPoint,
        perpendicularPoint,
      } = getRelevantPoints({
        AStart: a1Start,
        AEnd: a1End,
        BEnd: b1End,
        BStart: b1Start,
        AThickness: ta,
        BThickness: tb,
        AAngle: ninetyDeg,
        BAngle: oneEightyDeg,
      })
      const {
        beta: beta2,
        absoluteCross: ac2,
        absoluteDot: ad2,
        dotAB: d2,
        cross: c2,
        symmetricalPerpendicularPoint: spp2,
        perpendicularPoint: pp2,
      } = getRelevantPoints({
        AStart: b1Start,
        AEnd: b1End,
        BEnd: a2End,
        BStart: a2Start,
        AThickness: ta,
        BThickness: tb,
        AAngle: oneEightyDeg,
        BAngle: -ninetyDeg,
      })

      store.items.set(
        wall1id,
        getBaseWall(
          {
            showAngle: false,
            visible: true,
            prevWall: wall4id,
            nextWall: wall2id,
            areas: new Set([areaId]),
            thickness: sizesA.thickness,
            rotation: ninetyDeg,
            x: wb + ta,
            z: 0,
            id: wall1id,
            start: a1Start,
            end: a1End,
            height,
            width: sizesA.width,
            beta,
            absoluteCross,
            absoluteDot,
            cross,
            dot: dotAB,
            symmetricalPerpendicularPoint,
            perpendicularPoint,
            prevDot: d2,
          },
          store,
        ),
      )
      store.items.set(
        wall2id,
        getBaseWall(
          {
            visible: true,
            prevWall: wall1id,
            nextWall: wall3id,
            areas: new Set([areaId]),
            thickness: sizesB.thickness,
            height,
            start: b1Start,
            end: b1End,
            rotation: oneEightyDeg,
            z: wa + tb,
            x: 0,
            id: wall2id,
            width: sizesB.width,
            innerAngle: ninetyDeg,
            prevDot: dotAB,
            prevCross: cross,
            dot: d2,
            beta: beta2,
            absoluteCross: ac2,
            absoluteDot: ad2,
            cross: c2,
            symmetricalPerpendicularPoint: spp2,
            perpendicularPoint: pp2,
          },
          store,
        ),
      )

      store.items.set(
        wall3id,
        getBaseWall(
          {
            showAngle: false,
            visible: false,
            prevWall: wall2id,
            nextWall: wall4id,
            areas: new Set([areaId]),
            // thickness: 0.1,
            // height: 1,
            start: a2Start,
            end: a2End,
            rotation: -ninetyDeg,
            // width: sizesA.width,
            id: wall3id,
            x: -wb - ta,
            // z: 0,
            y: 9000,
          },
          store,
        ),
      )
      store.items.set(
        wall4id,
        getBaseWall(
          {
            showAngle: false,
            visible: false,
            prevWall: wall3id,
            nextWall: wall1id,
            areas: new Set([areaId]),
            // thickness: 0.1,
            // height: 1,
            start: b2Start,
            end: b2End,
            z: -wa - tb,
            id: wall4id,
            // width: sizesB.width,
            // x: 0,
            y: 9000,
          },
          store,
        ),
      )
      store.areas.set(
        areaId,
        getBaseArea({
          id: areaId,
          sides: [wall1id, wall2id, wall3id, wall4id],
          isClosed: true,
        }),
      )
      store.areas.set(
        areaId2,
        getBaseArea({
          id: areaId2,
          isRoof: true,
          sides: [wall1id, wall2id, wall3id, wall4id],
          isClosed: true,
          height,
        }),
      )

      if (
        !store.items.has('lights_baseSceneLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseSceneLight')
      ) {
        drawLight('lights_baseSceneLight', '', 'LUZGENERAL', height)
      }
      // const lightMatrix2 = new Matrix4()
      //   .clone()
      //   .setPosition(toVector3([20, 4000, -20]))
      //   .makeRotationFromQuaternion(new Quaternion(0, 0, 0, 1))

      if (
        !store.items.has('lights_baseAreaLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseAreaLight')
      ) {
        drawLight('lights_baseAreaLight', '', 'Luzgeneral2', height)
      } else {
        const base = store.items.get('lights_baseAreaLight')
        // if (isLight(base)) {
        //   // base.matrix = lightMatrix2
        // }
      }
      store.layers[store.currentLayer].items.add(wall1id).add(wall2id).add(wall3id).add(wall4id)
      store.layers[store.currentLayer].areas.add(areaId).add(areaId2)
      store.cameraCenter = [0, 4000, 0]
    })
  },
  drawLRightRoom: (
    sizesA: Record<string, number>,
    sizesB: Record<string, number>,
    sizesC: Record<string, number>,
    sizesD: Record<string, number>,
    sizesE: Record<string, number>,
    sizesF: Record<string, number>,
    height: number,
  ) => {
    const drawLight = (
      id: string,
      icon: string,
      catalogID: string,
      height?: number,
      matrix?: Matrix4,
    ) => get().drawLight(id, icon, catalogID, false, height, matrix)

    set((store) => {
      const [wall1id, wall2id, wall3id, wall4id, wall5id, wall6id] = [
        getId('walls'),
        getId('walls'),
        getId('walls'),
        getId('walls'),
        getId('walls'),
        getId('walls'),
      ]
      const areaId = getId('areas')
      const areaId2 = getId('areas')

      const wa = (sizesC.width + sizesE.width) / 2
      const ta = sizesA.thickness / 2

      const wb = (sizesD.width + sizesF.width) / 2
      const tb = sizesB.thickness / 2

      const wc = sizesC.width / 2
      const tc = sizesC.thickness / 2

      const wd = sizesD.width / 2
      const td = sizesD.thickness / 2

      const we = sizesE.width / 2
      const te = sizesE.thickness / 2

      const wf = sizesF.width / 2
      const tf = sizesF.thickness / 2

      const aStart = [wb, -wa] satisfies [number, number]
      const aEnd = [wb, wa] satisfies [number, number]

      const bStart = [wb, wa] satisfies [number, number]
      const bEnd = [-wb, wa] satisfies [number, number]

      const cStart = [-wb, wa] satisfies [number, number]
      const cEnd = [-wb, we - wc] satisfies [number, number]

      const dStart = [-wb, we - wc] satisfies [number, number]
      const dEnd = [wd - wf, we - wc] satisfies [number, number]

      const eStart = [wd - wf, we - wc] satisfies [number, number]
      const eEnd = [wd - wf, -wa] satisfies [number, number]

      const fStart = [wd - wf, -wa] satisfies [number, number]
      const fEnd = [wb, -wa] satisfies [number, number]

      const {
        beta,
        absoluteCross,
        absoluteDot,
        dotAB,
        cross,
        symmetricalPerpendicularPoint,
        perpendicularPoint,
      } = getRelevantPoints({
        AStart: aStart,
        AEnd: aEnd,
        BEnd: bEnd,
        BStart: bStart,
        AThickness: ta,
        BThickness: tb,
        AAngle: ninetyDeg,
        BAngle: oneEightyDeg,
      })

      const {
        beta: beta2,
        absoluteCross: ac2,
        absoluteDot: ad2,
        dotAB: d2,
        cross: c2,
        symmetricalPerpendicularPoint: spp2,
        perpendicularPoint: pp2,
      } = getRelevantPoints({
        AStart: bStart,
        AEnd: bEnd,
        BEnd: cEnd,
        BStart: cStart,
        AThickness: tb,
        BThickness: tc,
        AAngle: oneEightyDeg,
        BAngle: -ninetyDeg,
      })

      const {
        beta: beta3,
        absoluteCross: ac3,
        absoluteDot: ad3,
        dotAB: d3,
        cross: c3,
        symmetricalPerpendicularPoint: spp3,
        perpendicularPoint: pp3,
      } = getRelevantPoints({
        AStart: cStart,
        AEnd: cEnd,
        BEnd: dEnd,
        BStart: dStart,
        AThickness: tc,
        BThickness: td,
        AAngle: -ninetyDeg,
        BAngle: 0,
      })

      const {
        beta: beta4,
        absoluteCross: ac4,
        absoluteDot: ad4,
        dotAB: d4,
        cross: c4,
        symmetricalPerpendicularPoint: spp4,
        perpendicularPoint: pp4,
      } = getRelevantPoints({
        AStart: dStart,
        AEnd: dEnd,
        BEnd: eEnd,
        BStart: eStart,
        AThickness: td,
        BThickness: te,
        AAngle: 0,
        BAngle: -ninetyDeg,
      })

      const {
        beta: beta5,
        absoluteCross: ac5,
        absoluteDot: ad5,
        dotAB: d5,
        cross: c5,
        symmetricalPerpendicularPoint: spp5,
        perpendicularPoint: pp5,
      } = getRelevantPoints({
        AStart: eStart,
        AEnd: eEnd,
        BEnd: fEnd,
        BStart: fStart,
        AThickness: te,
        BThickness: tf,
        AAngle: -ninetyDeg,
        BAngle: 0,
      })

      const {
        beta: beta6,
        absoluteCross: ac6,
        absoluteDot: ad6,
        dotAB: d6,
        cross: c6,
        symmetricalPerpendicularPoint: spp6,
        perpendicularPoint: pp6,
      } = getRelevantPoints({
        AStart: fStart,
        AEnd: fEnd,
        BEnd: aEnd,
        BStart: aStart,
        AThickness: tf,
        BThickness: ta,
        AAngle: 0,
        BAngle: ninetyDeg,
      })
      store.items.set(
        wall1id,
        getBaseWall(
          {
            prevWall: wall6id,
            nextWall: wall2id,
            areas: new Set([areaId]),
            thickness: sizesA.thickness,
            rotation: ninetyDeg,
            x: wb + ta,
            z: 0,
            id: wall1id,
            start: aStart,
            end: aEnd,
            height,
            width: sizesC.width + sizesE.width,
            innerAngle: ninetyDeg,
            beta,
            absoluteCross,
            absoluteDot,
            cross,
            dot: dotAB,
            symmetricalPerpendicularPoint,
            perpendicularPoint,
            prevDot: dotAB,
          },
          store,
        ),
      )

      store.items.set(
        wall2id,
        getBaseWall(
          {
            prevWall: wall1id,
            nextWall: wall3id,
            areas: new Set([areaId]),
            thickness: sizesB.thickness,
            height,
            start: bStart,
            end: bEnd,
            rotation: oneEightyDeg,
            z: wa + tb,
            x: 0,
            id: wall2id,
            width: sizesD.width + sizesF.width,
            innerAngle: ninetyDeg,
            beta: beta2,
            absoluteCross: ac2,
            absoluteDot: ad2,
            cross: c2,
            dot: d2,
            symmetricalPerpendicularPoint: spp2,
            perpendicularPoint: pp2,
            prevDot: d2,
          },
          store,
        ),
      )
      store.items.set(
        wall3id,
        getBaseWall(
          {
            prevWall: wall2id,
            nextWall: wall4id,
            areas: new Set([areaId]),
            thickness: sizesC.thickness,
            beta: beta3,
            absoluteCross: ac3,
            absoluteDot: ad3,
            dot: d3,
            cross: c3,
            symmetricalPerpendicularPoint: spp3,
            perpendicularPoint: pp3,
            prevDot: d3,
            height,
            start: cStart,
            end: cEnd,
            rotation: -ninetyDeg,
            width: sizesC.width,
            id: wall3id,
            x: -wb - tb,
            z: wa - wc,
            innerAngle: ninetyDeg,
          },
          store,
        ),
      )
      store.items.set(
        wall4id,
        getBaseWall(
          {
            prevWall: wall3id,
            nextWall: wall5id,
            areas: new Set([areaId]),
            thickness: sizesD.thickness,
            beta: beta4,
            absoluteCross: ac4,
            absoluteDot: ad4,
            dot: d4,
            cross: c4,
            symmetricalPerpendicularPoint: spp4,
            perpendicularPoint: pp4,
            prevDot: d4,
            height,
            rotation: 0,
            start: dStart,
            end: dEnd,
            z: cEnd[1] - td,
            id: wall4id,
            width: sizesD.width,
            x: wd - wb,
            innerAngle: ninetyDeg,
          },
          store,
        ),
      )
      store.items.set(
        wall5id,
        getBaseWall(
          {
            prevWall: wall4id,
            nextWall: wall6id,
            areas: new Set([areaId]),
            thickness: sizesE.thickness,
            beta: beta5,
            absoluteCross: ac5,
            absoluteDot: ad5,
            dot: d5,
            cross: c5,
            symmetricalPerpendicularPoint: spp5,
            perpendicularPoint: pp5,
            prevDot: d5,
            height,
            start: eStart,
            rotation: twoSeventyDeg,
            end: eEnd,
            z: cEnd[1] - wd,
            id: wall5id,
            width: sizesE.width,
            x: -wb + wd * 2 - tf,
            innerAngle: oneEightyDeg + ninetyDeg,
          },
          store,
        ),
      )
      store.items.set(
        wall6id,
        getBaseWall(
          {
            prevWall: wall5id,
            nextWall: wall1id,
            areas: new Set([areaId]),
            thickness: sizesF.thickness,
            beta: beta6,
            absoluteCross: ac6,
            absoluteDot: ad6,
            dot: d6,
            cross: c6,
            symmetricalPerpendicularPoint: spp6,
            perpendicularPoint: pp6,
            prevDot: d6,
            height,
            rotation: 0,
            start: fStart,
            end: fEnd,
            z: -wa - tf,
            id: wall6id,
            width: sizesF.width,
            x: wb - wf,
            innerAngle: ninetyDeg,
          },
          store,
        ),
      )
      store.areas.set(
        areaId,
        getBaseArea({
          id: areaId,
          sides: [wall1id, wall2id, wall3id, wall4id, wall5id, wall6id],
          isClosed: true,
        }),
      )
      store.areas.set(
        areaId2,
        getBaseArea({
          id: areaId2,
          isRoof: true,
          sides: [wall1id, wall2id, wall3id, wall4id, wall5id, wall6id],
          isClosed: true,
          height,
        }),
      )
      if (
        !store.items.has('lights_baseSceneLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseSceneLight')
      ) {
        drawLight('lights_baseSceneLight', '', 'LUZGENERAL', height)
      }
      // const lightMatrix2 = new Matrix4()
      //   .clone()
      //   .setPosition(toVector3([20, 4000, -20]))
      //   .makeRotationFromQuaternion(new Quaternion(0, 0, 0, 1))

      if (
        !store.items.has('lights_baseAreaLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseAreaLight')
      ) {
        drawLight('lights_baseAreaLight', '', 'Luzgeneral2', height)
      } else {
        const base = store.items.get('lights_baseAreaLight')
        // if (isLight(base)) {
        //   base.matrix = lightMatrix2
        // }
      }
      store.layers[store.currentLayer].items
        .add(wall1id)
        .add(wall2id)
        .add(wall3id)
        .add(wall4id)
        .add(wall5id)
        .add(wall6id)
      store.layers[store.currentLayer].areas.add(areaId).add(areaId2)
      store.cameraCenter = [0, 4000, 0]
    })
  },
  drawLLeftRoom: (
    sizesA: Record<string, number>,
    sizesB: Record<string, number>,
    sizesC: Record<string, number>,
    sizesD: Record<string, number>,
    sizesE: Record<string, number>,
    sizesF: Record<string, number>,
    height: number,
  ) => {
    const drawLight = (
      id: string,
      icon: string,
      catalogID: string,
      height?: number,
      matrix?: Matrix4,
    ) => get().drawLight(id, icon, catalogID, false, height, matrix)

    set((store) => {
      const [wall1id, wall2id, wall3id, wall4id, wall5id, wall6id] = [
        getId('walls'),
        getId('walls'),
        getId('walls'),
        getId('walls'),
        getId('walls'),
        getId('walls'),
      ]
      const areaId = getId('areas')
      const areaId2 = getId('areas')

      const wa = (sizesC.width + sizesE.width) / 2
      const ta = sizesA.thickness / 2

      const wb = sizesB.width / 2
      const tb = sizesB.thickness / 2

      const wc = sizesC.width / 2
      const tc = sizesC.thickness / 2

      const wd = sizesD.width / 2
      const td = sizesD.thickness / 2

      const we = sizesE.width / 2
      const te = sizesE.thickness / 2

      const wf = (sizesB.width + sizesD.width) / 2
      const tf = sizesF.thickness / 2

      const aStart = [wf, -wa] satisfies [number, number]
      const aEnd = [wf, wa] satisfies [number, number]

      const bStart = [wf, wa] satisfies [number, number]
      const bEnd = [-wb + wd, wa] satisfies [number, number]

      const cStart = [-wb + wd, wa] satisfies [number, number]
      const cEnd = [-wb + wd, wa - wc - wd] satisfies [number, number]

      const dStart = [-wb + wd, wa - wc - wd] satisfies [number, number]
      const dEnd = [-wa, wa - wc - wd] satisfies [number, number]

      const eStart = [-wa, wa - we - wc] satisfies [number, number]
      const eEnd = [-wf, -wa] satisfies [number, number]

      const fStart = [-wf, -wa] satisfies [number, number]
      const fEnd = [wf, -wa] satisfies [number, number]

      const {
        beta,
        absoluteCross,
        absoluteDot,
        dotAB,
        cross,
        symmetricalPerpendicularPoint,
        perpendicularPoint,
      } = getRelevantPoints({
        AStart: aStart,
        AEnd: aEnd,
        BEnd: bEnd,
        BStart: bStart,
        AThickness: ta,
        BThickness: tb,
        AAngle: ninetyDeg,
        BAngle: oneEightyDeg,
      })

      const {
        beta: beta2,
        absoluteCross: ac2,
        absoluteDot: ad2,
        dotAB: d2,
        cross: c2,
        symmetricalPerpendicularPoint: spp2,
        perpendicularPoint: pp2,
      } = getRelevantPoints({
        AStart: bStart,
        AEnd: bEnd,
        BEnd: cEnd,
        BStart: cStart,
        AThickness: tb,
        BThickness: tc,
        AAngle: oneEightyDeg,
        BAngle: -ninetyDeg,
      })
      const {
        beta: beta3,
        absoluteCross: ac3,
        absoluteDot: ad3,
        dotAB: d3,
        cross: c3,
        symmetricalPerpendicularPoint: spp3,
        perpendicularPoint: pp3,
      } = getRelevantPoints({
        AStart: cStart,
        AEnd: cEnd,
        BEnd: dEnd,
        BStart: dStart,
        AThickness: tc,
        BThickness: td,
        AAngle: -ninetyDeg,
        BAngle: oneEightyDeg,
      })
      const {
        beta: beta4,
        absoluteCross: ac4,
        absoluteDot: ad4,
        dotAB: d4,
        cross: c4,
        symmetricalPerpendicularPoint: spp4,
        perpendicularPoint: pp4,
      } = getRelevantPoints({
        AStart: dStart,
        AEnd: dEnd,
        BEnd: eEnd,
        BStart: eStart,
        AThickness: td,
        BThickness: te,
        AAngle: oneEightyDeg,
        BAngle: -ninetyDeg,
      })

      const {
        beta: beta5,
        absoluteCross: ac5,
        absoluteDot: ad5,
        dotAB: d5,
        cross: c5,
        symmetricalPerpendicularPoint: spp5,
        perpendicularPoint: pp5,
      } = getRelevantPoints({
        AStart: eStart,
        AEnd: eEnd,
        BEnd: fEnd,
        BStart: fStart,
        AThickness: te,
        BThickness: tf,
        AAngle: -ninetyDeg,
        BAngle: 0,
      })

      const {
        beta: beta6,
        absoluteCross: ac6,
        absoluteDot: ad6,
        dotAB: d6,
        cross: c6,
        symmetricalPerpendicularPoint: spp6,
        perpendicularPoint: pp6,
      } = getRelevantPoints({
        AStart: fStart,
        AEnd: fEnd,
        BEnd: aEnd,
        BStart: aStart,
        AThickness: tf,
        BThickness: ta,
        AAngle: 0,
        BAngle: ninetyDeg,
      })

      store.items.set(
        wall1id,
        getBaseWall(
          {
            prevWall: wall6id,
            nextWall: wall2id,
            areas: new Set([areaId]),
            thickness: sizesA.thickness,
            rotation: ninetyDeg,
            x: wf + ta,
            z: 0,
            id: wall1id,
            start: aStart,
            end: aEnd,
            height,
            width: sizesC.width + sizesE.width,
            innerAngle: ninetyDeg,
            prevDot: dotAB,
            prevCross: cross,
            dot: dotAB,
            beta,
            absoluteCross,
            absoluteDot,
            cross,
            symmetricalPerpendicularPoint,
            perpendicularPoint,
          },
          store,
        ),
      )
      store.items.set(
        wall2id,
        getBaseWall(
          {
            prevWall: wall1id,
            nextWall: wall3id,
            areas: new Set([areaId]),
            thickness: sizesB.thickness,
            height,
            start: bStart,
            end: bEnd,
            rotation: oneEightyDeg,
            z: wa + tf,
            x: -wb + wf,
            id: wall2id,
            width: sizesB.width,
            innerAngle: ninetyDeg,
            prevDot: d2,
            prevCross: c2,
            dot: d2,
            beta: beta2,
            absoluteCross: ac2,
            absoluteDot: ad2,
            cross: c2,
            symmetricalPerpendicularPoint: spp2,
            perpendicularPoint: pp2,
          },
          store,
        ),
      )
      store.items.set(
        wall3id,
        getBaseWall(
          {
            prevWall: wall2id,
            nextWall: wall4id,
            areas: new Set([areaId]),
            thickness: sizesC.thickness,
            height,
            start: cStart,
            end: cEnd,
            rotation: -ninetyDeg,
            width: sizesC.width,
            id: wall3id,
            x: -wf + wd * 2 - tc,
            z: wa - wc,
            innerAngle: ninetyDeg,
            prevDot: d3,
            prevCross: c3,
            dot: d3,
            beta: beta3,
            absoluteCross: ac3,
            absoluteDot: ad3,
            cross: c3,
            symmetricalPerpendicularPoint: spp3,
            perpendicularPoint: pp3,
          },
          store,
        ),
      )

      store.items.set(
        wall4id,
        getBaseWall(
          {
            prevWall: wall3id,
            nextWall: wall5id,
            areas: new Set([areaId]),
            thickness: sizesD.thickness,
            height,
            start: dStart,
            end: dEnd,
            rotation: oneEightyDeg,
            x: (-wb + wd + -wa) / 2,
            z: wa - wc - wd + td,
            id: wall4id,
            width: sizesD.width,
            innerAngle: (3 * Math.PI) / 2,
            prevDot: d4,
            prevCross: c4,
            dot: d4,
            beta: beta4,
            absoluteCross: ac4,
            absoluteDot: ad4,
            cross: c4,
            symmetricalPerpendicularPoint: spp4,
            perpendicularPoint: pp4,
          },
          store,
        ),
      )
      store.items.set(
        wall5id,
        getBaseWall(
          {
            prevWall: wall4id,
            nextWall: wall6id,
            areas: new Set([areaId]),
            thickness: sizesE.thickness,
            height,
            start: eStart,
            rotation: twoSeventyDeg,
            end: eEnd,
            id: wall5id,
            width: sizesE.width,
            z: -wa + we,
            x: -wf - tf,
            innerAngle: ninetyDeg,
            prevDot: d5,
            prevCross: c5,
            dot: d5,
            beta: beta5,
            absoluteCross: ac5,
            absoluteDot: ad5,
            cross: c5,
            symmetricalPerpendicularPoint: spp5,
            perpendicularPoint: pp5,
          },
          store,
        ),
      )
      store.items.set(
        wall6id,
        getBaseWall(
          {
            prevWall: wall5id,
            nextWall: wall1id,
            areas: new Set([areaId]),
            thickness: sizesF.thickness,
            height,
            rotation: 0,
            start: fStart,
            end: fEnd,
            z: -wa - tf,
            id: wall6id,
            width: sizesB.width + sizesD.width,
            x: 0,
            innerAngle: ninetyDeg,
            prevDot: d6,
            prevCross: c6,
            dot: d6,
            beta: beta6,
            absoluteCross: ac6,
            absoluteDot: ad6,
            cross: c6,
            symmetricalPerpendicularPoint: spp6,
            perpendicularPoint: pp6,
          },
          store,
        ),
      )
      store.areas.set(
        areaId,
        getBaseArea({
          id: areaId,
          sides: [wall1id, wall2id, wall3id, wall4id, wall5id, wall6id],
          isClosed: true,
        }),
      )

      store.areas.set(
        areaId2,
        getBaseArea({
          id: areaId2,
          isRoof: true,
          sides: [wall1id, wall2id, wall3id, wall4id, wall5id, wall6id],
          isClosed: true,
          height,
        }),
      )
      if (
        !store.items.has('lights_baseSceneLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseSceneLight')
      ) {
        drawLight('lights_baseSceneLight', '', 'LUZGENERAL', height)
      }

      if (
        !store.items.has('lights_baseAreaLight') ||
        !store.layers[store.currentLayer].items.has('lights_baseAreaLight')
      ) {
        drawLight('lights_baseAreaLight', '', 'Luzgeneral2', height)
      } else {
        const base = store.items.get('lights_baseAreaLight')
        // if (isLight(base)) {
        //   base.matrix = lightMatrix2
        // }
      }
      store.layers[store.currentLayer].items
        .add(wall1id)
        .add(wall2id)
        .add(wall3id)
        .add(wall4id)
        .add(wall5id)
        .add(wall6id)
      store.layers[store.currentLayer].areas.add(areaId).add(areaId2)
      store.cameraCenter = [0, 4000, 0]
    })
  },

  setBeingDrawnVertex: (item: Store['vertexBeingDragged']) => {
    set((store) => {
      store.vertexBeingDragged = item
    })
  },
  addInnerAngle: (oldId: string, newId: string) => {
    set((store) => {
      const oldWall = store.items.get(oldId) as Walls
      const newWall = store.items.get(newId) as Walls
      if (!oldWall || !newWall)
        return console.error('Error on finding a wall to add a inner angle.')
      const [oldStart, oldEnd] = [oldWall.start, oldWall.end]
      const [newStart, newEnd] = [newWall.start, newWall.end]
      newWall.innerAngle = getInnerAngle(oldStart, oldEnd, newStart, newEnd).innerAngle
    })
  },
  snapToWall: (snapToId: string) => {
    set((store: Store) => {
      const currItem = store.items.get(store.currentObjectID)
      if (!isWall(currItem)) return
      const oldItem = store.items.get(snapToId)
      if (!isWall(oldItem)) return /* console.warn('SnapTo Item not found.') */
      currItem.end = oldItem.start
    })
  },
  setCurrentFirstWall: (id: string) => {
    set((store) => {
      store.currentFirstWall = id
    })
  },
  addHole: (id: string, holeID: string) => {
    set((store) => {
      const item = store.items.get(id) as Walls
      if (!item) return // console.log('wall not found')
      item.holes?.add(holeID)
    })
  },
  removeHole: (id: string, holeID: string) => {
    set((store) => {
      const item = store.items.get(id) as Walls
      if (!item) return // console.log('wall not found')
      item.holes?.delete(holeID)
    })
  },
  updateWallHoles: (id: string, optional?: number) => {
    const updateHole = (_id: string) => get().updateHole(_id, optional)
    set((store) => {
      const wall = store.items.get(id) as Walls
      if (!wall) return // console.log('Wall not found on updating holes')
      const wallHoles = wall.holes
      wallHoles.forEach((holeID) => {
        const hole = store.items.get(holeID)
        if (!isHole(hole))
          return // console.log('Not hole found on updateWallHoles:', { hole, holeID, wallHoles })
        updateHole(holeID)
      })
    })
  },
  updateWallMorphs: (id: string) => {
    set((store) => {
      const wall = store.items.get(id)
      if (!isWall(wall)) return
      const wallHoles = wall.holes
      if (wallHoles.size === 0) return
      wallHoles.forEach((holeID) => {
        const hole = store.items.get(holeID)
        if (!isHole(hole)) return get().editMorph(holeID, 'Fondo', wall.thickness)
      })
    })
  },
  breakWall: (id: string) => {
    let success = true
    set((store) => {
      const rightWall = store.items.get(id)
      if (!isWall(rightWall)) return // console.log('Not a wall provided when splitting')
      let hasHolesInMiddle = false
      const rigthWallHalfWidth = Math.floor(rightWall.width / 2)
      const start = toVector3(rightWall.start)
      const end = toVector3(rightWall.end)
      const startToEndVector = end.clone().sub(start)
      const projectedPoint = startToEndVector.clone().multiplyScalar(0.5)
      const previousWall = store.items.get(rightWall.prevWall)
      const associatedAreas = rightWall.areas ? Array.from(rightWall.areas) : []
      const nextWall = rightWall.nextWall ? store.items.get(rightWall.nextWall) : undefined
      const leftWallHalfWidth = rightWall.width - rigthWallHalfWidth
      const rotation = rightWall.rotation
      const thickness = rightWall.thickness / 2
      const threeQuarters = startToEndVector.clone().multiplyScalar(0.75)
      const oneFourth = startToEndVector.multiplyScalar(0.25)
      const proyectedPlusStart = [
        projectedPoint.x + start.x,
        projectedPoint.z + start.z,
      ] satisfies [number, number]
      const newId = getId('walls')
      const leftWall = getBaseWall(
        {
          id: newId,
          width: leftWallHalfWidth,
          start: rightWall.start,
          end: [...proyectedPlusStart],
          height: rightWall.height,
          isFirst: rightWall.isFirst,
          x: oneFourth.x + start.x + thickness * Math.sin(rotation),
          z: oneFourth.z + start.z - thickness * Math.cos(rotation),
          rotation,
        },
        store,
      )

      const allHoles = new Set<string>()
      rightWall.holes.forEach((holeID) => allHoles.add(holeID))
      rightWall.holes.clear()

      allHoles.forEach((holeID) => {
        const hole = store.items.get(holeID)
        if (!hole) return
        const holeStart = hole.distI
        const holeEnd = hole.distI + hole.width
        const holeCenter = hole.distI + hole.width / 2
        if (
          (holeStart < rigthWallHalfWidth && holeEnd > rigthWallHalfWidth) ||
          holeCenter === rigthWallHalfWidth
        ) {
          rightWall.holes.add(holeID)
          hasHolesInMiddle = true
        } else {
          if (holeCenter > rigthWallHalfWidth) {
            hole.wall = rightWall.id
            rightWall.holes.add(holeID)
            hole.distI = hole.distI - rigthWallHalfWidth
            hole.distD = rigthWallHalfWidth - (hole.distI + hole.width)
          } else {
            hole.wall = leftWall.id
            leftWall.holes.add(holeID)
            hole.distD = leftWall.width - (hole.distI + hole.width)
          }
        }
      })

      if (hasHolesInMiddle) {
        success = false
        return
      }

      rightWall.start = [...proyectedPlusStart]
      leftWall.end = [...proyectedPlusStart]
      rightWall.x = threeQuarters.x + start.x + thickness * Math.sin(rotation)
      rightWall.z = threeQuarters.z + start.z - thickness * Math.cos(rotation)

      leftWall.prevWall = rightWall.prevWall
      if (previousWall) {
        previousWall.nextWall = newId
        leftWall.innerAngle = rightWall.innerAngle
        leftWall.prevWall = previousWall.id
      }
      if (nextWall) {
        nextWall.prevWall = id
        rightWall.nextWall = nextWall.id
      }
      rightWall.prevWall = newId
      leftWall.nextWall = id

      rightWall.prevWall = newId
      rightWall.innerAngle = oneEightyDeg

      rightWall.width = rigthWallHalfWidth
      const allWalls = new Set<string>()

      rightWall.highItems.forEach((el) => allWalls.add(el))
      rightWall.lowItems.forEach((el) => allWalls.add(el))
      rightWall.highItems.clear()
      rightWall.lowItems.clear()

      allWalls.forEach((el) => {
        const item = store.items.get(el) as Item
        const itemPropertiesWidth = item.morphs.find(
          (el) => el.id === 'width' || el.name === 'Ancho',
        )
        const elDistI = start
          .clone()
          .sub(toVector3([item.x, item.z]))
          .length()

        const itemWidth = itemPropertiesWidth?._default ?? itemPropertiesWidth?.default ?? 0

        const isObjectInLeftHalf = elDistI <= leftWallHalfWidth
        const doesItemFit = elDistI + itemWidth <= leftWallHalfWidth

        if (isObjectInLeftHalf && doesItemFit) {
          item.snappedWall = leftWall.id

          if (item.tipoNormalizado === 'MAL') {
            leftWall.highItems.add(el)
          } else if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
            leftWall.lowItems.add(el)
          }
        } else if (!isObjectInLeftHalf) {
          item.snappedWall = rightWall.id

          if (item.tipoNormalizado === 'MAL') {
            rightWall.highItems.add(el)
          } else if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
            rightWall.lowItems.add(el)
          }
        } else {
          item.snappedWall = ''
          leftWall.highItems.delete(el)
          leftWall.lowItems.delete(el)
          rightWall.highItems.delete(el)
          rightWall.lowItems.delete(el)
        }
      })

      if (associatedAreas) {
        associatedAreas.forEach((areaID) => {
          const currArea = store.areas.get(areaID)
          if (!isArea(currArea)) return
          const originalSides = currArea.sides
          const updatedSides: string[] = []
          originalSides.forEach((sideId) => {
            if (sideId === rightWall.id) {
              updatedSides.push(leftWall.id)
              updatedSides.push(rightWall.id)
            } else {
              updatedSides.push(sideId)
            }
          })
          currArea.sides = updatedSides

          store.areas.set(areaID, getBaseArea(currArea))
        })
        leftWall.areas = new Set(associatedAreas)
        rightWall.areas = new Set(associatedAreas)
      }
      store.items.set(newId, leftWall)
      store.layers[store.currentLayer].items.add(newId)
    })
    return success
  },
})
